import app from 'flarum/forum/app';
// ⚠️ 本仓既有写法是 `flarum/common/Component` (⛔ **不是** `flarum/common/components/Component`) ——
//   核心的模块 id 就是 `common/Component`; 写成后者拿到 undefined ⇒
//   `class X extends undefined` ⇒ "Class extends value undefined" ⇒ 整个扩展的 initializer 挂掉。
import Component from 'flarum/common/Component';
// v0.18.80 — 筛选下拉一律用 Flarum 原生 Dropdown (⛔ 不手搓下拉; 见 brief §7)
import Dropdown from 'flarum/common/components/Dropdown';
import Button from 'flarum/common/components/Button';
import Api from '../utils/Api';
// 金额格式 / 档位 class / 档位序数词 —— 全部复用既有 helper (⛔ 前端不自己写 toFixed, 不建 tier 映射表)
import { formatAmount, formatDelta, tierClassName, tierOrdinalText } from '../utils/refundFormat';
// v0.19.20 (批 41 · 段②) — 「后端错误文案」改用**共用入口**
//   (本仓铁律「一个逻辑一个入口, 不许抄两遍」; 改前这个原语在本文件里抄了一份)。
//   ⚠️ 改前本文件那一份是**短版** (没有 `response.json.error` 分支, 兜底文案硬编码) ——
//     统一到共用入口那版 (超集) 后, 本文件的兜底文案改为**显式传参** `errText(e, '退费列表加载失败')`。
import { errText } from '../utils/FormPrimitives';
// 学员头像 —— 复用既有具名导出 (内部走 app.store 取 user; ⛔ 不手搓头像)
import { UserAvatarLink } from './StudentsListTab';
// 审批弹层 —— 复用既有 modal (⛔ 本 tab 不自建审批弹层, 也不新增审批端点)
import RefundApproveModal from '../modal/RefundApproveModal';

/**
 * v0.18.80 (辉哥 10/24 拍板) — 「退费管理」tab (设计稿 ux-refund-mgmt-20261024 · S1)
 *
 * ## 数据来源 (批 A 的只读端点, 一个数都不许前端自己算)
 *   `GET /api/ziiven/scheduler/refunds`
 *     → { summary{pending_count, month_refund_amount, month_request_count, month},
 *         can_approve, pending[], records{items,total,page,per_page,pages} }
 *
 * ## ⛔ 三个最容易做错的点 (批 A 实际返回体 ⇒ 照实际写)
 *   ① **发起人角色**在 `card.refund_requested_by_role` (顶层**没有** `requested_by_role` 字段 ——
 *      批 A 已把顶层那份删了, 同一逻辑只维护一处)。
 *   ② `RefundView::cardFields()` **不含** `student_display_name` ⇒ 喂 `RefundApproveModal` 前
 *      **必须自己补** `card.student_display_name = item.student_name`
 *      (⛔ 不补的话审批弹层里学员名回落成「学员」)。
 *   ③ **事件对象没有稳定 id** (同一张卡可能两行: 驳回后再申请) ⇒ 列表 key 用
 *      `card_id + '_' + status` (两张卡同状态时再加后缀去重, 见 `recordKeys()`)。
 *
 * ## ⛔ 颜色纪律 (设计稿 §1.2)
 *   新元素里凡是「文字」的地方, 一律只用 `--zs-text-1` / `--zs-text-2`;
 *   语义色 (绿/琥珀/紫/橙) 只落在图标 / 描边 / 底色上。
 *   原因: 浅色主题下 `--zs-success-34c759` / `--zs-warning-fbbf24` / `--zs-info-60a5fa`
 *   压在浅底上只有 2.8:1 / 3.1:1 / 4.4:1 ⇒ 达不到 AA 正文 4.5:1。
 */

/** 每页条数 —— 与后端 `ListRefundsController::DEFAULT_PER_PAGE` 同值 (⛔ 后端还会 clamp) */
const PER_PAGE = 20;

/** 搜索框防抖 (ms) —— ⛔ 不防抖的话每敲一个字打一次端点 */
const SEARCH_DEBOUNCE = 350;

/** 状态筛选项 (值 = 后端 status 枚举; ⛔ 文案跟后端 `status_label` 一致) */
const STATUS_OPTIONS = [{
  value: 'all',
  labelKey: 'ziven-scheduler.forum.refund-mgmt.filter-all-status'
}, {
  value: 'pending',
  labelKey: 'ziven-scheduler.forum.refund-mgmt.filter-status-pending'
}, {
  value: 'approved',
  labelKey: 'ziven-scheduler.forum.refund-mgmt.filter-status-approved'
}, {
  value: 'rejected',
  labelKey: 'ziven-scheduler.forum.refund-mgmt.filter-status-rejected'
}];

/** 原因筛选项 (值 = 后端 `RefundCalculator::REASONS`; ⛔ 不另造一套原因枚举) */
const REASON_OPTIONS = [{
  value: 'all',
  labelKey: 'ziven-scheduler.forum.refund-mgmt.filter-all-reasons'
}, {
  value: 'institution',
  labelKey: 'ziven-scheduler.forum.refund-mgmt.filter-reason-institution'
}, {
  value: 'consumer',
  labelKey: 'ziven-scheduler.forum.refund-mgmt.filter-reason-consumer'
}, {
  value: 'mutual',
  labelKey: 'ziven-scheduler.forum.refund-mgmt.filter-reason-mutual'
}];

/** 发起人角色 → 中文 (与既有 `RefundApproveModal` 的 ROLE_LABELS 同口径; ⛔ 那个文件零改动) */
const ROLE_LABELS = {
  coach: '教练',
  sales: '销售',
  manager: '店长',
  student: '学员'
};

/** `YYYY-MM-DD` (本地时区) —— 设计稿默认「本月 1 号 ~ 今天」 */
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return y + '-' + m + '-' + day;
}
export default class RefundMgmtTab extends Component {
  oninit(vnode) {
    super.oninit(vnode);

    // 校区下拉的候选项 = **当前用户可见的校区** (由父组件透传; 那份列表后端已按
    // `SchoolContext::visibleSchoolIds()` 收窄 ⇒ ⛔ 这里绝不列全站校区)
    this.schools = vnode.attrs.schools || [];
    this.loading = true;
    this.error = '';
    this.data = null;

    // ── 筛选默认值 (设计稿 §7 自定项 ⑫: 校区/状态/原因 = 全部, 日期 = 本月 1 号 ~ 今天) ──
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    this.filters = {
      school_id: '',
      status: 'all',
      reason: 'all',
      date_from: ymd(monthStart),
      date_to: ymd(now),
      q: ''
    };
    this.page = 1;
    this._searchTimer = null;
    this.load();
  }
  onremove(vnode) {
    super.onremove(vnode);
    if (this._searchTimer) {
      clearTimeout(this._searchTimer);
      this._searchTimer = null;
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // 数据
  // ─────────────────────────────────────────────────────────────────────

  /** 拉数据 (⛔ 后端已排好序: 申请时间倒序 ⇒ 前端**不再**排一遍) */
  async load() {
    this.loading = true;
    this.error = '';
    m.redraw();
    try {
      const r = await Api.refunds(this.buildParams());
      this.data = r || null;
      this.preloadStudents();
    } catch (e) {
      this.data = null;
      this.error = errText(e, '退费列表加载失败');
    } finally {
      this.loading = false;
      m.redraw();
    }
  }

  /**
   * 把事件里的学员**预先灌进 `app.store`** ⇒ 头像能拿到 User 模型。
   *
   * ## 为什么必须自己灌
   *   既有 `UserAvatarLink` 的逻辑是: `getById('users', id)` 拿不到 ⇒ 自己 `store.find()` ⇒
   *   但 Flarum 2.0 的 `Store` **不触发 `m.redraw()`** ⇒ 那次 find 回来之后,
   *   没有任何人重绘 ⇒ `UserAvatarLink` 永远停在第一次渲染的 `<Avatar user={null} />`
   *   ⇒ 页面上是一个**没有字母、没有底色**的空圆 (设计稿 §2 S1 ③ 要求的是原生 `.Avatar` 字母头像)。
   *   ⇒ 这里在数据到位后主动把学员拉进 store, 全部拉完后 `m.redraw()` 一次。
   *
   * ⚠️ 刻意**不改**既有的 `UserAvatarLink` (它在学员列表页能正常工作, 因为那份响应自带 User 模型;
   *    改它会波及别的 tab)。
   */
  preloadStudents() {
    const ids = [];
    const push = id => {
      if (id && ids.indexOf(id) === -1) ids.push(id);
    };
    this.pendingItems().forEach(it => push(it.student_id));
    (this.records().items || []).forEach(it => push(it.student_id));
    if (!ids.length) return;
    let pending = ids.length;
    const done = () => {
      pending -= 1;
      if (pending <= 0) m.redraw();
    };
    ids.forEach(id => {
      try {
        const p = app.store.find('users', id);
        if (p && typeof p.then === 'function') {
          p.then(done, done);
        } else {
          done();
        }
      } catch (e) {
        done();
      }
    });
  }

  /** 请求参数 (参数名与批 A §2.3 一致) */
  buildParams() {
    const f = this.filters;
    const params = {
      page: this.page,
      per_page: PER_PAGE
    };
    if (f.school_id) params.school_id = f.school_id;
    if (f.status && f.status !== 'all') params.status = f.status;
    if (f.reason && f.reason !== 'all') params.reason = f.reason;
    if (f.date_from) params.date_from = f.date_from;
    if (f.date_to) params.date_to = f.date_to;
    if (f.q) params.q = f.q;
    return params;
  }

  /** 后端错误的 `detail` 文案 (⛔ 不自己编文案)
   *  ⚠️ v0.19.20 (批 41 · 段②) — 实现**搬**进共用入口 `utils/FormPrimitives.js`
   *    (改前 `errText` 在本文件里抄了一份 ⇒ 本仓铁律「一个逻辑一个入口」)
   *    ⇒ 兜底文案改为调用处显式传入 `errText(e, '退费列表加载失败')` (逐字不变)。 */

  // ─────────────────────────────────────────────────────────────────────
  // 交互
  // ─────────────────────────────────────────────────────────────────────

  /** 改筛选 ⇒ page 归零重查 (⛔ 不在前端过滤, 一律重发请求) */
  setFilter(key, value) {
    this.filters[key] = value;
    this.page = 1;
    this.load();
  }

  /** 搜索框 (⛔ 防抖; 回车/失焦也立刻查) */
  onSearchInput(value) {
    this.filters.q = value;
    if (this._searchTimer) clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(() => {
      this._searchTimer = null;
      this.page = 1;
      this.load();
    }, SEARCH_DEBOUNCE);
  }
  onSearchCommit() {
    if (this._searchTimer) {
      clearTimeout(this._searchTimer);
      this._searchTimer = null;
    }
    this.page = 1;
    this.load();
  }

  /** 重置 ⇒ 回到默认筛选 (本月 1 号 ~ 今天 + 三个「全部」) */
  resetFilters() {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    this.filters = {
      school_id: '',
      status: 'all',
      reason: 'all',
      date_from: ymd(monthStart),
      date_to: ymd(now),
      q: ''
    };
    this.page = 1;
    this.load();
  }
  setPage(page) {
    if (page < 1) return;
    if (this.data && this.data.records && this.data.records.pages && page > this.data.records.pages) return;
    this.page = page;
    this.load();
  }

  /**
   * 打开审批弹层 (⛔ 复用既有 `RefundApproveModal` —— 它自己拉预览、填审批备注、
   * 批完自己弹回执页 ⇒ 本 tab **不再**弹任何东西)。
   *
   * ⚠️ `item.card` = 批 A 带的 `RefundView::cardFields()` 全量 ⇒ 前端**基本不用映射**。
   * ⚠️ **必须补 `student_display_name`** —— `cardFields()` 不含它 (批 A §0.5 ②),
   *    ⛔ 不补的话弹层里学员名回落成「学员」。
   * ⚠️ v0.18.81 — `cardFields()` **也不含** `template_name` / `remaining_lessons`
   *    ⇒ 弹层里 `card.remaining_lessons` = undefined ⇒ 页面出现「剩余 undefined 课时」。
   *    ⇒ 在这里补两个字段 (⛔ 不许改 `RefundApproveModal.js`, ⛔ 不许改 `RefundView::cardFields()`):
   *      · `template_name`  = 顶层 `item.card_name` (批 A 顶层 card_name = join card_templates.name)
   *        ⇒ 为 null (卡种被删) 时弹层自己 fallback「学员卡 #<id>」, 不崩。
   *      · `remaining_lessons` = 顶层 `item.remaining_lessons` (与 `CardDetailModal` 同一个口径)
   *        ⇒ 不限次卡时后端给 **null** ⇒ JSX 里 `{null}` 渲染成空 ⇒ 弹层那行显示「剩余  课时」
   *           (⚠️ 弹层那行文案是**既有代码**, 本批零改动) ⇒ 但**绝不再是 undefined**。
   */
  openApprove(item) {
    const card = Object.assign({}, item.card, {
      student_display_name: item.student_name,
      template_name: item.card_name,
      remaining_lessons: item.remaining_lessons
    });
    app.modal.show(RefundApproveModal, {
      card,
      onDone: () => this.load()
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 取值小工具 (⛔ 一个数都不许前端自己算)
  // ─────────────────────────────────────────────────────────────────────

  summary() {
    return this.data && this.data.summary || {};
  }
  canApprove() {
    return !!(this.data && this.data.can_approve === true);
  }
  pendingItems() {
    return this.data && this.data.pending || [];
  }
  records() {
    return this.data && this.data.records || {};
  }

  /**
   * 列表 key。
   *
   * ⛔ **事件对象没有稳定 id** (批 A §0.5 ③) ⇒ 基准 key = `card_id + '_' + status`。
   * ⛔ 但同一张卡**可能**出现两次同状态 (反复「申请 → 驳回」多轮) ⇒
   *    Mithril 的 key 重复会直接抛 "In fragments, vnodes must have unique keys"
   *    ⇒ 这里只在**真的撞了**的时候加后缀, 基准 key 保持 brief 规定的那个。
   */
  recordKeys(items) {
    const seen = {};
    const keys = [];
    items.forEach(item => {
      const base = String(item.card_id) + '_' + String(item.status);
      let key = base;
      let n = 0;
      while (seen[key]) {
        n += 1;
        key = base + '_' + n;
      }
      seen[key] = true;
      keys.push(key);
    });
    return keys;
  }

  /** 档位 pill: class 按 `tier_order` (1→tier-1 / 2→tier-2 / 3→tier-3);
   *  `tier_order` 为 0/未知 ⇒ ⛔ 不加档位类、显示「—」 */
  tierInfo(item) {
    const order = Number(item.tier_order);
    if (!isFinite(order) || order < 1) {
      return {
        cls: '',
        text: '—',
        order: 0,
        title: item.tier_label || null
      };
    }
    return {
      cls: tierClassName(item.tier_order),
      text: tierOrdinalText(item.tier_order) || '—',
      order: Math.floor(order),
      // 后端下发的完整审批口径 (「财务审批 · 金额较大」) 挂 title ⇒ 不占 pill 宽度
      title: item.tier_label || null
    };
  }

  /**
   * 差额格。
   *
   * ⛔ `delta = 实退额 − 系统核算额` (正 = 多退 / 负 = 少退, 与生产 `RefundResultModal` 同口径)。
   * ⛔ 展示**绝对值** + 方向词 (与生产「多退 ¥60」/「少退 ¥140」一致)。
   * ⛔ `delta = 0` ⇒ 数值仍 `--zs-text-1` (⛔ 不用绿色, 绿色 = 通过语义), **不挂**方向词。
   * ⛔ `delta = null` ⇒ 显示「—」 (⛔ 不许算成 0, 那会显示成「没差额」)。
   */
  deltaInfo(item) {
    const raw = item.delta;
    if (raw === null || raw === undefined || raw === '') {
      return {
        value: '—',
        dir: '',
        cls: ''
      };
    }
    const n = Number(raw);
    if (!isFinite(n)) {
      return {
        value: '—',
        dir: '',
        cls: ''
      };
    }
    if (Math.abs(n) < 0.005) {
      return {
        value: '¥' + formatAmount(0),
        dir: '',
        cls: 'is-zero'
      };
    }
    return {
      value: '¥' + formatDelta(n),
      dir: n > 0 ? app.translator.trans('ziven-scheduler.forum.refund-mgmt.over') : app.translator.trans('ziven-scheduler.forum.refund-mgmt.under'),
      cls: 'is-diff'
    };
  }

  /** 金额格 (系统核算额 / 实退额) —— null ⇒ 「—」 */
  moneyText(value) {
    if (value === null || value === undefined || value === '') return '—';
    const n = Number(value);
    if (!isFinite(n)) return '—';
    return '¥' + formatAmount(n);
  }

  /** 分页按钮序列 (页数多时只露窗口, 首尾页永远在) */
  pageList() {
    const records = this.records();
    const pages = Number(records.pages) || 0;
    const cur = Number(records.page) || 1;
    if (pages <= 7) {
      const all = [];
      for (let i = 1; i <= pages; i += 1) all.push(i);
      return all;
    }
    const out = [1];
    const from = Math.max(2, cur - 1);
    const to = Math.min(pages - 1, cur + 1);
    if (from > 2) out.push('…');
    for (let i = from; i <= to; i += 1) out.push(i);
    if (to < pages - 1) out.push('…');
    out.push(pages);
    return out;
  }

  // ─────────────────────────────────────────────────────────────────────
  // 渲染
  // ─────────────────────────────────────────────────────────────────────

  /** 页面头 (图标 + 标题 + 副标题 + 3 个概览格) */
  headView() {
    const s = this.summary();
    return m("div", {
      className: "ZS-refundmgmt-head"
    }, m("div", {
      className: "ZS-refundmgmt-head-ic"
    }, m("i", {
      className: "fas fa-file-invoice-dollar"
    })), m("div", {
      className: "ZS-refundmgmt-head-main"
    }, m("div", {
      className: "ZS-refundmgmt-head-title"
    }, app.translator.trans('ziven-scheduler.forum.tab-refunds')), m("div", {
      className: "ZS-refundmgmt-head-sub"
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.subtitle'))), m("div", {
      className: "ZS-refundmgmt-kpis"
    }, m("div", {
      className: "ZS-refundmgmt-kpi"
    }, m("span", {
      className: "kpi-num"
    }, s.pending_count === undefined ? 0 : s.pending_count), m("span", {
      className: "kpi-label"
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.kpi-pending'))), m("div", {
      className: "ZS-refundmgmt-kpi"
    }, m("span", {
      className: "kpi-num"
    }, '¥' + formatAmount(s.month_refund_amount === undefined || s.month_refund_amount === null ? 0 : s.month_refund_amount)), m("span", {
      className: "kpi-label"
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.kpi-month-amount'))), m("div", {
      className: "ZS-refundmgmt-kpi"
    }, m("span", {
      className: "kpi-num"
    }, s.month_request_count === undefined ? 0 : s.month_request_count), m("span", {
      className: "kpi-label"
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.kpi-month-requests')))));
  }

  /** 筛选条 (下拉 / 日期 / 搜索 全部用 Flarum 原生组件形态) */
  filterView() {
    const f = this.filters;
    const schoolLabel = f.school_id ? (this.schools.find(s => String(s.id) === String(f.school_id)) || {}).name || f.school_id : app.translator.trans('ziven-scheduler.forum.refund-mgmt.filter-all-schools');
    const statusLabel = app.translator.trans((STATUS_OPTIONS.find(o => o.value === f.status) || STATUS_OPTIONS[0]).labelKey);
    const reasonLabel = app.translator.trans((REASON_OPTIONS.find(o => o.value === f.reason) || REASON_OPTIONS[0]).labelKey);
    return m("div", {
      className: "ZS-refundmgmt-filter"
    }, m("span", {
      className: "fld"
    }, m("span", {
      className: "ZS-refundmgmt-filter-label"
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.filter-school')), m(Dropdown, {
      buttonClassName: "Button ZS-refundmgmt-filter-btn",
      lazyDraw: true,
      label: schoolLabel
    }, m(Button, {
      active: !f.school_id,
      onclick: () => this.setFilter('school_id', '')
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.filter-all-schools')), this.schools.map(s => m(Button, {
      key: s.id,
      active: String(s.id) === String(f.school_id),
      onclick: () => this.setFilter('school_id', String(s.id))
    }, s.name)))), m("span", {
      className: "fld"
    }, m("span", {
      className: "ZS-refundmgmt-filter-label"
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.filter-status')), m(Dropdown, {
      buttonClassName: "Button ZS-refundmgmt-filter-btn",
      lazyDraw: true,
      label: statusLabel
    }, STATUS_OPTIONS.map(o => m(Button, {
      key: o.value,
      active: f.status === o.value,
      onclick: () => this.setFilter('status', o.value)
    }, app.translator.trans(o.labelKey))))), m("span", {
      className: "fld"
    }, m("span", {
      className: "ZS-refundmgmt-filter-label"
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.filter-reason')), m(Dropdown, {
      buttonClassName: "Button ZS-refundmgmt-filter-btn",
      lazyDraw: true,
      label: reasonLabel
    }, REASON_OPTIONS.map(o => m(Button, {
      key: o.value,
      active: f.reason === o.value,
      onclick: () => this.setFilter('reason', o.value)
    }, app.translator.trans(o.labelKey))))), m("span", {
      className: "fld fld-date"
    }, m("span", {
      className: "ZS-refundmgmt-filter-label"
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.filter-date')), m("span", {
      className: "mk-daterange"
    }, m("input", {
      type: "date",
      className: "FormControl ZS-refundmgmt-date",
      value: f.date_from,
      "aria-label": app.translator.trans('ziven-scheduler.forum.refund-mgmt.date-from'),
      onchange: e => this.setFilter('date_from', e.target.value)
    }), m("span", null, "~"), m("input", {
      type: "date",
      className: "FormControl ZS-refundmgmt-date",
      value: f.date_to,
      "aria-label": app.translator.trans('ziven-scheduler.forum.refund-mgmt.date-to'),
      onchange: e => this.setFilter('date_to', e.target.value)
    }))), m("span", {
      className: "mk-spacer"
    }), m("input", {
      type: "search",
      className: "FormControl ZS-refundmgmt-search",
      value: f.q,
      placeholder: app.translator.trans('ziven-scheduler.forum.refund-mgmt.search-placeholder'),
      "aria-label": app.translator.trans('ziven-scheduler.forum.refund-mgmt.search-placeholder'),
      oninput: e => this.onSearchInput(e.target.value),
      onchange: () => this.onSearchCommit()
    }), m(Button, {
      className: "Button Button--flat",
      onclick: () => this.resetFilters()
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.reset')));
  }

  /** 状态徽标 (三态; ⛔ 文字一律 --zs-text-1, 语义色只落在底/边/图标) */
  stateView(item) {
    const status = item.status;
    const cls = 'ZS-refundmgmt-state' + (status === 'pending' ? ' is-pending' : status === 'approved' ? ' is-approved' : status === 'rejected' ? ' is-rejected' : '');
    const icon = status === 'pending' ? 'fa-hourglass' : status === 'approved' ? 'fa-circle-check' : status === 'rejected' ? 'fa-ban' : 'fa-circle-question';
    return m("span", {
      className: cls
    }, m("i", {
      className: 'fas ' + icon
    }), item.status_label || status || '—');
  }

  /** 原因徽标 (⛔ 文字 --zs-text-1; 底/边走品牌蓝) */
  reasonView(item) {
    return m("span", {
      className: "ZS-refundmgmt-reason"
    }, item.reason_label || '—');
  }

  /** 档位 pill */
  tierView(item) {
    const t = this.tierInfo(item);
    return m("span", {
      className: 'ZS-refundmgmt-tier ' + t.cls,
      title: t.title || undefined
    }, t.text);
  }

  /** 金额格 */
  amtBox(label, value, extraCls, dir) {
    return m("div", {
      className: 'ZS-refundmgmt-amtbox ' + (extraCls || '')
    }, m("span", {
      className: "amt-label"
    }, label), m("span", {
      className: "amt-val"
    }, value, dir ? m("span", {
      className: "amt-dir"
    }, dir) : null));
  }

  /** 待处理申请卡 */
  pendingCardView(item) {
    const tier = this.tierInfo(item);
    const delta = this.deltaInfo(item);
    const card = item.card || {};
    const role = ROLE_LABELS[card.refund_requested_by_role] || '';
    // ⚠️ v0.18.81 — 「申请说明」读**事件顶层**的 `item.note`
    //   (改前读 `card.refund_note` ⇒ `RefundView::cardFields()` **不含**这个键 ⇒ 恒为空 ⇒ 说明永远不显示)
    //   后端只在 **pending** 事件上给 `note` (已结算事件的说明在数据层取不到 ⇒ 宁可不显示)。
    const note = item.note || '';
    return m("div", {
      className: "ZS-refundmgmt-card",
      key: String(item.card_id) + '_' + String(item.status)
    }, m("div", {
      className: "ZS-refundmgmt-cardtop"
    }, m("span", {
      className: "ZS-avatar ZS-avatar--sm ZS-refundmgmt-avatar"
    }, m(UserAvatarLink, {
      userId: item.student_id
    })), m("span", {
      className: "ZS-refundmgmt-student"
    }, item.student_name || '—'), m("span", {
      className: "ZS-refundmgmt-school"
    }, item.school_name || '—'), this.reasonView(item), this.tierView(item)), m("div", {
      className: "ZS-refundmgmt-cardmeta"
    }, item.card_name || '—', ' · 已消 ', item.cleared_lessons === null || item.cleared_lessons === undefined ? '—' : item.cleared_lessons + ' 节', ' · 剩余 ', item.remaining_lessons === null || item.remaining_lessons === undefined ? '不限次' : item.remaining_lessons + ' 节'), m("div", {
      className: "ZS-refundmgmt-amounts"
    }, this.amtBox(app.translator.trans('ziven-scheduler.forum.refund-mgmt.amount-system'), this.moneyText(item.system_amount)), this.amtBox(app.translator.trans('ziven-scheduler.forum.refund-mgmt.amount-real'), this.moneyText(item.amount)), this.amtBox(app.translator.trans('ziven-scheduler.forum.refund-mgmt.amount-delta'), delta.value, delta.cls, delta.dir)), m("div", {
      className: "ZS-refundmgmt-reqline"
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.requested-at'), ' ', m("b", null, item.requested_at || '—'), ' · ', app.translator.trans('ziven-scheduler.forum.refund-mgmt.requested-by'), ' ', m("b", null, item.requested_by_name || '—'), role ? '（' + role + '）' : null, note ? ' · ' + app.translator.trans('ziven-scheduler.forum.refund-mgmt.requested-note') + '「' + note + '」' : null), tier.order >= 2 ? m("div", {
      className: "ZS-refundmgmt-tiernote"
    }, m("i", {
      className: "fas fa-circle-exclamation"
    }), m("span", null, app.translator.trans('ziven-scheduler.forum.refund-mgmt.tiernote'))) : null, this.canApprove() ? m("div", {
      className: "ZS-refundmgmt-actions"
    }, m(Button, {
      className: "Button Button--reject",
      onclick: () => this.openApprove(item)
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.reject')), m(Button, {
      className: "Button Button--approve",
      onclick: () => this.openApprove(item)
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.approve'))) : m("div", {
      className: "ZS-refundmgmt-locknote"
    }, m("i", {
      className: "fas fa-lock"
    }), m("span", null, app.translator.trans('ziven-scheduler.forum.refund-mgmt.locknote'))));
  }

  /** 退费记录 · 桌面表格 (⛔ 记录行**只显示实退额**, 没有「系统核算额」列 —— 辉哥拍的 C 项) */
  tableView(items) {
    const keys = this.recordKeys(items);
    return m("div", {
      className: "ZS-refundmgmt-tablewrap"
    }, m("table", {
      className: "ZS-refundmgmt-table"
    }, m("thead", null, m("tr", null, m("th", null, app.translator.trans('ziven-scheduler.forum.refund-mgmt.col-requested-at')), m("th", null, app.translator.trans('ziven-scheduler.forum.refund-mgmt.col-student')), m("th", null, app.translator.trans('ziven-scheduler.forum.refund-mgmt.col-school')), m("th", null, app.translator.trans('ziven-scheduler.forum.refund-mgmt.col-card')), m("th", null, app.translator.trans('ziven-scheduler.forum.refund-mgmt.col-reason')), m("th", null, app.translator.trans('ziven-scheduler.forum.refund-mgmt.col-tier')), m("th", {
      style: "text-align:right"
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.col-amount')), m("th", null, app.translator.trans('ziven-scheduler.forum.refund-mgmt.col-status')), m("th", null, app.translator.trans('ziven-scheduler.forum.refund-mgmt.col-approver')))), m("tbody", null, items.map((item, i) => m("tr", {
      key: keys[i]
    }, m("td", {
      className: "ZS-refundmgmt-table-time"
    }, item.requested_at || '—'), m("td", null, item.student_name || '—'), m("td", null, item.school_name || '—'), m("td", null, item.card_name || '—'), m("td", null, this.reasonView(item)), m("td", null, this.tierView(item)), m("td", {
      className: "ZS-refundmgmt-table-amt"
    }, this.moneyText(item.amount)), m("td", null, this.stateView(item)), m("td", null, item.approved_by_name || '—'))))));
  }

  /** 退费记录 · 移动端卡片式行 (⛔ 不做横向滚动表格) */
  mobileRowsView(items) {
    const keys = this.recordKeys(items);
    return m("div", {
      className: "ZS-refundmgmt-mrows"
    }, items.map((item, i) => m("div", {
      className: "ZS-refundmgmt-mrow",
      key: keys[i]
    }, m("div", {
      className: "mr-l"
    }, m("div", {
      className: "mr-time"
    }, item.requested_at || '—'), this.stateView(item)), m("div", {
      className: "mr-m"
    }, m("div", {
      className: "mr-name"
    }, item.student_name || '—', m("span", {
      className: "mr-school"
    }, item.school_name || '')), m("div", {
      className: "mr-card"
    }, m("span", {
      className: "mr-cardtxt"
    }, (item.card_name || '—') + ' · ' + (item.reason_label || '—')), this.tierView(item)), m("div", {
      className: "mr-by"
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.col-approver'), " ", item.approved_by_name || '—')), m("div", {
      className: "mr-r"
    }, this.moneyText(item.amount)))));
  }

  /** 分页 */
  pagerView() {
    const records = this.records();
    const pages = Number(records.pages) || 0;
    const page = Number(records.page) || 1;
    const perPage = Number(records.per_page) || PER_PAGE;
    if (pages <= 0) return null;
    return m("div", {
      className: "ZS-refundmgmt-pager"
    }, m("span", {
      className: "pg-info"
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.pager-info', {
      page: page,
      pages: pages
    })), m("button", {
      type: "button",
      className: "ZS-refundmgmt-page",
      disabled: page <= 1,
      "aria-label": app.translator.trans('ziven-scheduler.forum.refund-mgmt.pager-prev'),
      onclick: () => this.setPage(page - 1)
    }, m("i", {
      className: "fas fa-chevron-left"
    })), this.pageList().map((p, i) => p === '…' ? m("span", {
      className: "ZS-refundmgmt-page",
      key: 'gap' + i
    }, "\u2026") : m("button", {
      type: "button",
      key: p,
      className: 'ZS-refundmgmt-page' + (p === page ? ' is-current' : ''),
      onclick: () => this.setPage(p)
    }, p)), m("button", {
      type: "button",
      className: "ZS-refundmgmt-page",
      disabled: page >= pages,
      "aria-label": app.translator.trans('ziven-scheduler.forum.refund-mgmt.pager-next'),
      onclick: () => this.setPage(page + 1)
    }, m("i", {
      className: "fas fa-chevron-right"
    })), m("span", {
      className: "ZS-refundmgmt-page-size"
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.pager-size', {
      n: perPage
    })));
  }

  /** 空态卡 (⛔ 里面**不出现任何按钮** —— 生产没有「新建退费申请」端点) */
  emptyView(title, sub) {
    return m("div", {
      className: "ZS-refundmgmt-empty"
    }, m("i", {
      className: "fas fa-inbox"
    }), m("b", null, title), m("span", null, sub));
  }
  view() {
    if (this.loading && !this.data) {
      return m("div", {
        className: "ZS-refundmgmt"
      }, m("div", {
        className: "ZS-refundmgmt-empty"
      }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.loading')));
    }
    if (this.error) {
      return m("div", {
        className: "ZS-refundmgmt"
      }, this.headView(), m("div", {
        className: "ZS-refundmgmt-empty"
      }, m("i", {
        className: "fas fa-triangle-exclamation"
      }), m("b", null, this.error)));
    }
    const pending = this.pendingItems();
    const records = this.records();
    const items = records.items || [];
    return m("div", {
      className: "ZS-refundmgmt"
    }, this.headView(), this.filterView(), m("div", {
      className: "ZS-refundmgmt-sec-head"
    }, m("span", {
      className: "ZS-refundmgmt-sec-title"
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.sec-pending')), m("span", {
      className: "ZS-refundmgmt-count"
    }, pending.length)), pending.length > 0 ? pending.map(item => this.pendingCardView(item)) : this.emptyView(app.translator.trans('ziven-scheduler.forum.refund-mgmt.empty-pending-title'), app.translator.trans('ziven-scheduler.forum.refund-mgmt.empty-pending-sub')), m("div", {
      className: "ZS-refundmgmt-sec-head"
    }, m("span", {
      className: "ZS-refundmgmt-sec-title"
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.sec-records')), m("span", {
      className: "ZS-refundmgmt-count"
    }, app.translator.trans('ziven-scheduler.forum.refund-mgmt.count-records', {
      n: Number(records.total) || 0
    }))), items.length > 0 ? [this.tableView(items), this.mobileRowsView(items)] : this.emptyView(app.translator.trans('ziven-scheduler.forum.refund-mgmt.empty-records-title'), app.translator.trans('ziven-scheduler.forum.refund-mgmt.empty-records-sub')), this.pagerView());
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/components/RefundMgmtTab', RefundMgmtTab);